import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useCampaign, bannerTextFor } from "@/context/CampaignContext";

const DISMISS_KEY = "dcb_holiday_banner_dismissed";

// Decorative seed positions — animation-delay staggers them naturally
const SNOWFLAKES = Array.from({ length: 14 }, (_, i) => ({
  left: `${(i * 7.4 + 3) % 100}%`,
  delay: `${(i * 0.7) % 8}s`,
  duration: `${6 + (i % 5)}s`,
  size: 8 + (i % 3) * 3,
}));

// Falling pumpkins + ghosts — alternates between emoji so the stream feels mixed.
const HALLOWEEN_FALL = Array.from({ length: 14 }, (_, i) => ({
  left: `${(i * 7.4 + 3) % 100}%`,
  delay: `${(i * 0.65) % 9}s`,
  duration: `${7 + (i % 4)}s`,
  size: 11 + (i % 3) * 3,
  emoji: i % 2 === 0 ? "🎃" : "👻",
}));

const EASTER_DOTS = Array.from({ length: 12 }, (_, i) => ({
  left: `${(i * 8.5 + 4) % 100}%`,
  top: `${(i % 2 === 0 ? 18 : 65) + (i % 3) * 6}%`,
  hue: ["#F5C2D8", "#B8E0C7", "#F2E69A", "#D7C7F0"][i % 4],
  size: 6 + (i % 3) * 2,
}));

function ChristmasDecor() {
  return (
    <>
      {/* Falling snowflakes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        {SNOWFLAKES.map((s, i) => (
          <span
            key={i}
            className="absolute text-[#212121]/70 hidden sm:block"
            style={{
              left: s.left,
              top: "-12px",
              fontSize: `${s.size}px`,
              animation: `dcb-snow ${s.duration} linear ${s.delay} infinite`,
            }}
          >❄</span>
        ))}
      </div>
      {/* Tinsel gold sparkle along top + bottom edges */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#FFD37A] to-transparent opacity-80 pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[#FFD37A] to-transparent opacity-80 pointer-events-none" />
    </>
  );
}

function EasterDecor() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {EASTER_DOTS.map((d, i) => (
        <span
          key={i}
          className="absolute rounded-full hidden sm:block"
          style={{
            left: d.left,
            top: d.top,
            width: `${d.size}px`,
            height: `${d.size * 1.3}px`,
            background: d.hue,
            opacity: 0.6,
            transform: i % 2 ? "rotate(-15deg)" : "rotate(12deg)",
            animation: `dcb-easter-bob ${4 + (i % 3)}s ease-in-out ${i * 0.3}s infinite`,
          }}
        />
      ))}
    </div>
  );
}

function MothersDayDecor() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      <span className="hidden sm:block absolute left-4 top-1/2 -translate-y-1/2 opacity-70 text-lg">🌸</span>
      <span className="hidden sm:block absolute left-16 top-1/2 -translate-y-1/2 opacity-60 text-sm">🌷</span>
      <span className="hidden sm:block absolute right-16 top-1/2 -translate-y-1/2 opacity-60 text-sm">🌷</span>
      <span className="hidden sm:block absolute right-32 top-1/2 -translate-y-1/2 opacity-70 text-lg">🌸</span>
      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[#F8C4DC] to-transparent opacity-80" />
    </div>
  );
}

function HalloweenDecor() {
  return (
    <>
      {/* Falling pumpkins + ghosts — hidden on mobile */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        {HALLOWEEN_FALL.map((s, i) => (
          <span
            key={i}
            className="absolute hidden sm:block"
            style={{
              left: s.left,
              top: "-14px",
              fontSize: `${s.size}px`,
              animation: `dcb-snow ${s.duration} linear ${s.delay} infinite`,
              filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.6))",
              opacity: 0.85,
            }}
          >{s.emoji}</span>
        ))}
      </div>
      {/* Fiery orange edges */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#ff8c00] to-transparent opacity-90 pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[#ff8c00] to-transparent opacity-90 pointer-events-none" />
    </>
  );
}

function FathersDayDecor() {  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      <span className="hidden sm:block absolute left-6 top-1/2 -translate-y-1/2 opacity-70 text-base">🔨</span>
      <span className="hidden sm:block absolute right-6 top-1/2 -translate-y-1/2 opacity-70 text-base">🪵</span>
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage:
            "repeating-linear-gradient(90deg, transparent 0 38px, rgba(212,170,90,0.4) 38px 39px)",
        }}
      />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#D4AA5A] to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[#D4AA5A] to-transparent" />
    </div>
  );
}

const HOLIDAY_CONFIG = {
  christmas: {
    bg: "bg-[#1a472a]",
    text: "text-[#FFD37A]",
    decor: <ChristmasDecor />,
    leftIcon: "🎄",
    rightIcon: "🎄",
  },
  mothers_day: {
    bg: "bg-gradient-to-r from-[#A4326D] via-[#C95E9C] to-[#6B3B7D]",
    text: "text-[#212121]",
    decor: <MothersDayDecor />,
    leftIcon: null,
    rightIcon: null,
  },
  easter: {
    bg: "bg-gradient-to-r from-[#E6DEF7] via-[#F2F8D8] to-[#D7F0DF]",
    text: "text-[#3A2E10]",
    decor: <EasterDecor />,
    leftIcon: "🐰",
    rightIcon: "🥚",
  },
  fathers_day: {
    bg: "bg-gradient-to-r from-[#0F2545] via-[#1B3A6B] to-[#102746]",
    text: "text-[#212121]",
    decor: <FathersDayDecor />,
    leftIcon: null,
    rightIcon: null,
  },
  halloween: {
    bg: "bg-gradient-to-r from-[#000000] via-[#ff8c00] to-[#000000]",
    text: "text-[#FFF4E0]",
    decor: <HalloweenDecor />,
    leftIcon: "🎃",
    rightIcon: "👻",
  },
};

export default function HolidayBanner() {
  const { campaign } = useCampaign();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (campaign.active_holiday === "general") return;
    const stored = sessionStorage.getItem(DISMISS_KEY);
    setDismissed(stored === campaign.active_holiday);
  }, [campaign.active_holiday]);

  if (campaign.active_holiday === "general" || dismissed) return null;
  const text = bannerTextFor(campaign);
  const cfg = HOLIDAY_CONFIG[campaign.active_holiday];
  if (!text || !cfg) return null;

  const onDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, campaign.active_holiday);
    setDismissed(true);
  };

  return (
    <div
      className={`${cfg.bg} ${cfg.text} relative w-full overflow-hidden`}
      data-testid="holiday-banner"
      data-holiday={campaign.active_holiday}
    >
      {cfg.decor}
      <div className="max-w-7xl mx-auto px-4 sm:px-12 lg:px-16 py-2.5 sm:py-3 flex items-center justify-center gap-2 sm:gap-3 relative z-10">
        {cfg.leftIcon && <span className="hidden sm:inline text-lg" aria-hidden="true">{cfg.leftIcon}</span>}
        <p
          className="text-center text-sm sm:text-base font-medium leading-snug tracking-wide"
          data-testid="holiday-banner-text"
        >
          {text}
        </p>
        {cfg.rightIcon && <span className="hidden sm:inline text-lg" aria-hidden="true">{cfg.rightIcon}</span>}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss holiday banner"
        className={`absolute top-1/2 -translate-y-1/2 right-2 sm:right-4 p-1.5 z-20 opacity-80 hover:opacity-100 transition-opacity ${cfg.text}`}
        data-testid="holiday-banner-dismiss"
      >
        <X size={16} />
      </button>
    </div>
  );
}
